import { DevTool } from "@hookform/devtools";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import DataGrid from '../../components/common/dataGrid/dataGrid.js';
import ButtonWithSearch from '../../components/common/buttonWithSearch.js';
import { commonPostApi, commonGetApi } from "../../utils/apiService.js";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import { useSelector } from "react-redux";
import {
  arrageArrayFunc,
  convertToSearchField,
  generateColumnMetadata,
  Feature,
} from "../../utils/utils.js";
import PrimaryHeading from '../../components/common/primaryHeading.js';
import PrimarySideBar from '../../components/common/sideBar/primarySideBar.js';
import InputIterator from '../../components/common/inputIterator.js';
import AddButtonSection from '../../components/common/AddButtonSection.js';
import AddImageSection from '../../components/common/AddImageSection.jsx';
import CommonModal from '../../components/common/commonModel.js';
import UserDetails from "../profile/UserDetails.jsx"
import { userFormRender, assignRoleFormRender, UsersTableColumns } from "../../utils/serviceData.js";
import { showToast } from '../../components/common/toast.js';
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";
import { SORT_ORDER } from "../../constants/sortOrder";

export default function Users(props) {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm({mode: "onChange",reValidateMode: "onChange"});
  const campId = watch("camp");
  const organization_id = useSelector((state) => state?.common?.organization);
  const menuLeft = useRef(null);
  const [columns, setColumns] = useState([]);
  const [values, setValues] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loader, setLoader] = useState(false);
  const [addUserVisible, setAddUserVisible] = useState(false);
  const [existingUsernames, setExistingUsernames] = useState([]);
  const [editUserVisible, setEditUserVisible] = useState(false);
  const [assignRoleVisible, setAssignRoleVisible] = useState(false);
  const [visible, setVisible] = useState({});
  const [editUserDetails, setEditUserDetails] = useState({});
  const [uploadedImg, setUploadedImg] = useState(null);
  const searchFields = ["user_name", "first_name", "last_name","email","phone_no","address"];
  const [formRender, setFormRender] = useState(userFormRender);
  const [assignRole, setAssignRoleFormRender] =
    useState(assignRoleFormRender);
  const [selected, setSelected] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 1,
    sortBy: "user_name",
    sortDir: SORT_ORDER.ASC,
    searchCriteria: convertToSearchField(searchFields, ""),
  });
  const fields = [
    "user_name",
    "first_name",
    "last_name",
    "email",
    "address",
    "city",
    "district",
    "state",
    "pincode",
    "phone_no",
  ];



  function onPageChange(event) {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      first: event?.first,
      page: event.page + 1,
    }));
    init({
      ...navigator,
      first: event?.first,
      page: event.page + 1,
    });
  }

  const init = async (navigator) => {
    setInitLoading(true);
    let currentObj = {
      query: {
        page: navigator?.page,
        limit: navigator?.rows,
        searchCriteria: navigator?.searchCriteria,
      },
    };

    Promise.all([commonPostApi(API_ENDPOINTS.USER_DETAILS_LIST, currentObj),
      commonGetApi(API_ENDPOINTS.GET_EXISTING_USERS)
    ])
      .then(([listRes, existingRes]) => {
        const userData = listRes?.data?.data || [];

        setNavigator((prev) => ({
          ...prev,
          totalRecords: listRes?.data?.total || 0,
          page: listRes?.data?.currentPage || 1,
        }));

        setExistingUsernames(
          (existingRes?.data?.data?.rows || []).map((u) =>
            u?.user_name?.toLowerCase().trim()
          )
        );

        setColumns(
          arrageArrayFunc(generateColumnMetadata({ data: userData, defaultColumnsArr:UsersTableColumns }) || [], [
            "user_name",
            "email",
            "phone_no",
            "address",
          ])
        );
        setValues(userData);
        setLoading(false);
        setInitLoading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onSortClick = (event) => {
    setLoading(true);
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    setNavigator((prev) => ({
      ...prev,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    }));
    init({
      ...navigator,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    });
  };

  const onRowCountChange = (event) => {
    setLoading(true);
    const newRows = event?.value ?? navigator?.rows;
    const updated = {
      ...navigator,
      rows: newRows,
      page: 1,
      first: 0,
    };
    setNavigator(updated);
    init(updated);
  };

  const onSubmit = async (data) => {
    if (loader) return;
    setLoader(true);

    let finalData = data;
    let headers = { "Content-Type": "application/json" };

    if (uploadedImg && uploadedImg?.size > 0) {
      finalData = new FormData();
      finalData.append("profile_picture", uploadedImg);
      finalData.append("_payload", JSON.stringify(data));
      headers = { "Content-Type": "multipart/form-data" };
    }

    await commonPostApi(API_ENDPOINTS.ADD_USER_DETAILS, finalData, { headers })
      .then((data) => {
        if (data?.message) {
          showToast(TOAST_TYPE.ERROR, data?.response?.data?.message);
          setLoader(false);
        } else {
          showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.USER.ADDED);
          setAddUserVisible(false);
          init(navigator);
          reset();
          setUploadedImg(null);
          setLoader(false);
        }
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, err?.response?.data?.message ?? COMMON_MSG.ERROR.GENERIC);
        setLoader(false);
      });
  };

  const onEdit = async (data) => {
    if (loader) return;
    setLoader(true);

    // Build only changed fields using userFormRender (like addOrEditPatient.js)
    const validObj = { ...data };
    const newObj = {};
    Object.keys(dirtyFields).forEach((key) => {
      userFormRender.forEach((item) => {
        if (item.name === key) {
          newObj[item.fieldLabel] = validObj[key];
        } else if (item?.item) {
          item?.item?.forEach((subItem) => {
            if (subItem.name === key) {
              newObj[subItem.fieldLabel] = validObj[key];
            }
          });
        }
      });
    });
    newObj["user_id"] = editUserDetails?.user_id;

    let finalData = newObj;
    let headers = { "Content-Type": "application/json" };

    if (uploadedImg && uploadedImg?.size > 0) {
      finalData = new FormData();
      finalData.append("profile_picture", uploadedImg);
      finalData.append("_payload", JSON.stringify(newObj));
      headers = { "Content-Type": "multipart/form-data" };
    }

    await commonPostApi(
      API_ENDPOINTS.EDIT_USER_DETAILS,
      finalData,
      { headers }
    )
      .then((data) => {
        if (data?.message) {
          showToast(TOAST_TYPE.ERROR, data?.message);
          setLoader(false);
        } else {
          showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.USER.UPDATED);
          setEditUserVisible(false);
          init(navigator);
          reset();
          setUploadedImg(null);
          setLoader(false);
        }
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
        setLoader(false);
      });
  };

  const onDelete = async () => {
    let headers = {
      "Content-Type": "application/json",
    };
    await commonPostApi(
      API_ENDPOINTS.DELETE_USER_DETAILS,
      { user_id: visible?.id },
      {
        headers,
      }
    )
      .then((data) => {
        if (data?.status === 200) {
          showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.USER.DELETED);
          init(navigator);
          setVisible((prev) => ({
            ...prev,
            show: false,
          }));
        }
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
      });
  };

  const button = [
    {
      type: "primarySuccessButton",
      label: "Add User",
      icon: "pi pi-plus",
      class: "mt-2 w-full flex pr-4",
      onClick: () => {
        setAddUserVisible(true);
        setEditUserDetails({});
      },
    },
  ];
  const itemRenderer = (item, prop) => (
    <div className="p-menuitem-content">
      {item.label === "Assign Role" &&
        props?.page !== "role" &&
        Feature?.featureVerify(Feature.listOfFeature.userAddRole) && (
          <Button
            className="card-button-assign-role"
            label="Assign Role"
            onClick={() => {
              setAssignRoleVisible(true);
              setSelected(prop?.props?.itemProp?.user_id);
            }}
          />
        )}
      {item.label === "Edit" &&
        props?.page !== "role" &&
        Feature?.featureVerify(Feature.listOfFeature.userEdit) && (
          <Button
            className="card-button-edit-secound"
            label="Edit"
            onClick={() => {
              setEditUserVisible(true);
              const imgUrl = prop?.props?.itemProp.profile_picture
                ? `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.SEARCH_IMAGE_BY_ID(prop?.props?.itemProp?.user_id)}`
                : null;
              setEditUserDetails(prop?.props?.itemProp);
              setUploadedImg(imgUrl);

            }}
          />
        )}
      {item.label === "Delete" && (
        <Button
          className="card-button-delete"
          label="Delete"
          onClick={() => {
            setVisible({
              id: prop?.props?.itemProp?.user_id,
              show: true,
            });
          }}
        />
      )}
    </div>
  );

  const onAssignRole = async () => {
    if (loader) return;
    setLoader(true);
    let headers = {
      "Content-Type": "application/json",
    };
    let assignRole = {
      camp_details_id: watch("camp"),
      user_id: selected,
      app_role_id: watch("role"),
    };
    await commonPostApi(API_ENDPOINTS.ASSIGN_USER_ROLE, assignRole, {
      headers,
    })
      .then((data) => {
        if (data?.status === 200) {
          showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.USER.ASSIGNED_TO_CAMP);
          setAssignRoleVisible(false);
          reset();
          setLoader(false);
        } else if (
          data?.response?.data?.message ===
          "User is already assigned to this camp."
        ) {
          showToast(TOAST_TYPE.ERROR, FEATURE_MSG.USER.ALREADY_ASSIGNED);
          setLoader(false);
          setAssignRoleVisible(false);
          reset();
        } else {
          showToast(TOAST_TYPE.ERROR, data?.message ?? COMMON_MSG.ERROR.GENERIC);
          setLoader(false);
        }
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, err.response?.data?.message ?? COMMON_MSG.ERROR.GENERIC);
        setLoader(false);
      });
  };

  const AssignRolebuttons = [
    {
      type: "primarySuccessButton",
      label: "Submit",
      class: "col-4",
      disabled: !selected || loader,
      onClick: handleSubmit(onAssignRole),
    },
    {
      type: "primaryCancelButton",
      label: "Cancel",
      class: "col-4",
      onClick: () => {
        setAssignRoleVisible(false);
        setSelected(null);
        reset();
      }
    },
  ];

  const AddUserbuttons = [
    {
      type: "primarySuccessButton",
      label: "Submit",
      class: "col-4",
      disabled: (Object.keys(dirtyFields).length === 0 && !uploadedImg) || loader,
      onClick: handleSubmit(onSubmit),
    },
    {
      type: "primaryCancelButton",
      label: "Cancel",
      class: "col-4",
      onClick: () => {
        setAddUserVisible(false);
        setUploadedImg(null);
        reset();
      },
    },
  ];
  const EditUserbuttons = [
    {
      type: "primarySuccessButton",
      label: "Submit",
      class: "col-4",
      disabled: (Object.keys(dirtyFields).length === 0 && !uploadedImg) || loader,
      onClick: handleSubmit(onEdit),
    },
    {
      type: "primaryCancelButton",
      label: "Cancel",
      class: "col-4",
      onClick: () => {
        setEditUserVisible(false);
        setUploadedImg(null);
        setEditUserDetails({});
        reset();
      },
    },
  ];

  const submitButtons = [
    {
      type: "primarySuccessButton",
      label: "Yes",
      class: "w-8rem pr-3",
      onClick: () => {
        onDelete();
      },
    },
    {
      type: "secondaryButton",
      label: "No",
      class: "w-7rem",
      onClick: () => {
        setVisible((prev) => ({
          ...prev,
          show: false,
        }));
      },
    },
  ];
  function handleSearchChange(value) {
    setNavigator((prev) => ({
      ...prev,
      searchCriteria: convertToSearchField(searchFields, value),
    }));
    init({
      ...navigator,
      searchCriteria: convertToSearchField(searchFields, value),
    });
  }

  const onRowClick = (event) => {
    setSelectedUser(event?.data);
  };

  useEffect(() => {
    init(navigator);
    if (organization_id?.organization_id) {
      getAllByRole();
    }
  }, [organization_id]);

  useEffect(() => {
    init(navigator);
    if (campId) {
      getAllByRole(campId);
    }
  }, [campId]);

  const getAllByRole = async (campId) => {
    const campQuery = {
      query: {
        searchCriteria: {
          organization_id: organization_id?.organization_id,
        },
      },
    };
    Promise.all([
      commonPostApi(API_ENDPOINTS.CAMP_DETAILS, campQuery),
      campId
        ? commonPostApi(API_ENDPOINTS.GET_ALL_ROLES, { camp_details_id: campId })
        : Promise.resolve({ data: { data: [] } })
    ])
      .then(([campDetails, allRoles]) => {
        const campOptions = campDetails?.data?.data?.map((item) => ({
          label: item?.camp_name,
          value: item?.camp_details_id,
        }));
        const roleOptions = allRoles?.data?.data?.map((item) => ({
          label: item?.role_name,
          value: item?.app_role_id,
        }));
        setAssignRoleFormRender((prev) =>
          prev?.map((item) => {
            if (item?.name === "role") return { ...item, options: roleOptions };
            else if (item?.name === "camp")
              return { ...item, options: campOptions };
            return item;
          })
        );
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
      });
  };



  useEffect(() => {
    fields.forEach((fieldName) => {
      setValue(fieldName, editUserDetails?.[fieldName]);
    });
  }, [editUserDetails]);

  useEffect(() => {
    setFormRender((form) => {
      const passwordForm = userFormRender.filter((item) => item?.type === "password");
      const filteredForm = form.filter((item) => item?.type !== "password");
      if (editUserVisible) return filteredForm;
      filteredForm.splice(5, 0, ...passwordForm);
      return filteredForm;
    });
  }, [editUserVisible, addUserVisible]);

  useEffect(() => {
  setFormRender((prev) =>
    prev.map((field) => {
      if (field.name === "user_name") {
        return {
          ...field,
          rules: {
            ...field.rules,
            validate: (value) => {
              if (!value) return true;

              const normalized = value.toLowerCase().trim();

              if (
                editUserVisible &&
                normalized ===
                  editUserDetails?.user_name?.toLowerCase()?.trim()
              ) {
                return true;
              }

              return existingUsernames.includes(normalized)
                ? "Username already exists"
                : true;
            },
          },
        };
      }
      return field;
    })
  );
}, [ editUserVisible, editUserDetails]);


  return (
    <div className="container">
      <PrimaryHeading className={"mt-3 mb-2"} />
      {!selectedUser && (
        <DataGrid
          header={
            <ButtonWithSearch
              buttons={button}
              backButton={props?.backButton}
              searchHeaderLable={props?.searchHeaderLable ?? "Users"}
              searchPlaceHolder={"Search Users"}
              isSearchEnabled={true}
              isCalenderEnabled={false}
              isButtonEnabledLeft={false}
              isButtonEnabledRight={Feature?.featureVerify(
                Feature.listOfFeature.userAdd
              )}
              loading={initLoading}
              styles={{ gridTemplateColumns: "1fr 400px" }}
              onChange={handleSearchChange}
            />
          }
          onSortClick={onSortClick}
          onPageChange={onPageChange}
          onRowCountChange={onRowCountChange}
          onRowClick={onRowClick}
          isPaginatorEnabled={true}
          navigator={navigator}
          column={columns}
          value={values}
          ref={menuLeft}
          loading={loading}
          sortable
          optionsView={[
            {
              items: [
                {
                  label: "Assign Role",
                  icon: "pi pi-pencil",
                  template: itemRenderer,
                },
                {
                  label: "Edit",
                  icon: "pi pi-pencil",
                  template: itemRenderer,
                },
                {
                  label: "Delete",
                  icon: "pi pi-upload",
                  template: itemRenderer,
                },
              ],
            },
          ]}
        />
      )}
      {addUserVisible && (
        <PrimarySideBar
          visible={addUserVisible}
          onHide={() => {
            setAddUserVisible(false);
            setUploadedImg(null);
            reset();
          }}
          header={<div className="sideBar-header">Add User</div>}
        >
          <AddImageSection showImg={uploadedImg} setImg={setUploadedImg} />
          <InputIterator
            control={control}
            errors={errors}
            formRender={formRender}
          />
          <div className="pt-3">
            <AddButtonSection buttons={AddUserbuttons} />
          </div>
        </PrimarySideBar>
      )}
      {editUserVisible && (
        <PrimarySideBar
          visible={editUserVisible}
          onHide={() => {
            setEditUserVisible(false);
            setUploadedImg(null);
            setEditUserDetails({});
            reset();
          }}
          header={<div className="sideBar-header">Edit User</div>}
        >
          <AddImageSection showImg={uploadedImg} setImg={setUploadedImg} />
          <InputIterator
            control={control}
            errors={errors}
            formRender={formRender}
          />
          <div className="pt-3">
            <AddButtonSection buttons={EditUserbuttons} />
          </div>
        </PrimarySideBar>
      )}
      {assignRoleVisible && (
        <PrimarySideBar
          visible={assignRoleVisible}
          onHide={() => {
            setAssignRoleVisible(false);
            setSelected(null);
            reset();
          }}
          header={<div className="sideBar-header">Assign Role</div>}
        >
          <InputIterator
            control={control}
            errors={errors}
            formRender={assignRole}
          />
          <div className="pt-3">
            <AddButtonSection buttons={AssignRolebuttons} />
          </div>
        </PrimarySideBar>
      )}
      <CommonModal
        visible={visible?.show}
        header="User Patient"
        onHide={() => setVisible(false)}
      >
        <div>
          <div className="flex justify-content-center pr-4">
            Would you like to delete this user?
          </div>

          <div className="mt-6 ml-2">
            <AddButtonSection buttons={submitButtons} />
          </div>
        </div>
      </CommonModal>
      {selectedUser && (
        <UserDetails userDetailsData={selectedUser} setSelectedUser={setSelectedUser} />
      )}
      <DevTool control={control} />
    </div>
  );
}
